import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import { handleApiCall } from '../../helpers/handleApiCall';
import { DialogTypes, ErrorSeverity, Gender } from '../../enums';
import { openDialog } from '../../components/dialogs/DialogRoot/redux';
import { commonService } from '../../services/commonServiceInstance';
import { arePrivateNotesAvailable, getPrivateNotesChanges, rebasePrivateNotes } from './privateNotesChanges';

const MIN_RESULT_SIZE = 20;

const initialState = {
    isLoadingLookups: true,
    isSearching: false,
    lookups: {},
    nonPreferredProviderDialog: {
        isOpen: false,
        provider: null,
        warningMessage: null,
    },
    providerPrintDialog: {
        isOpen: false,
        healthcareProviderId: null,
    },
    providers: null,
    previousProviderIds: [],
    hasMoreResults: false,
    query: {},
    sortBy: null,
    sortDirection: null,
    sessionId: null,
    searchRequestId: null,
    privateNotesRevision: 0,
    privateNotesUpdates: {},
    privateNotesInteraction: {},
    privateNotesOpen: false,
    privateNotesEditor: null,
};

const slice = createSlice({
    name: 'advancedProviderSearch',
    initialState,
    reducers: {
        resetState() {
            return { ...initialState };
        },
        resetResults(state) {
            state.providers = null;
            state.previousProviderIds = [];
            state.hasMoreResults = false;
        },
        setSessionId(state, action) {
            state.sessionId = action.payload;
        },
        setSearchRequestId(state, action) {
            state.searchRequestId = action.payload;
        },
        setPrivateNotesInteraction(state, action) {
            state.privateNotesInteraction = action.payload;
        },
        startPrivateNotesEditing(state, action) {
            const { healthcareProviderId, privateNotes, canEdit } = action.payload;
            if (state.privateNotesEditor?.healthcareProviderId === healthcareProviderId) return;
            state.privateNotesEditor = {
                healthcareProviderId,
                originalNotes: privateNotes,
                notes: privateNotes,
                canEdit: canEdit && arePrivateNotesAvailable(privateNotes),
                draft: '',
                pendingEdit: null,
                isSaving: false,
                error: null,
                canRetry: false,
            };
        },
        editPrivateNotes(state, action) {
            const editor = state.privateNotesEditor;
            if (!editor?.canEdit || editor.isSaving) return;
            Object.assign(editor, action.payload, { error: null, canRetry: false });
        },
        setPrivateNotesSaveState(state, action) {
            if (state.privateNotesEditor) Object.assign(state.privateNotesEditor, action.payload);
        },
        acknowledgePrivateNotesChange(state, action) {
            const { privateNotes, completedKeys } = action.payload;
            const editor = state.privateNotesEditor;
            editor.notes = rebasePrivateNotes(editor, privateNotes, completedKeys);
            editor.originalNotes = privateNotes;
        },
        setPrivateNotesOpen(state, action) {
            state.privateNotesOpen = action.payload;
        },
        closePrivateNotes(state) {
            state.privateNotesInteraction = {};
            state.privateNotesOpen = false;
            state.privateNotesEditor = null;
        },
        updateProviderPrivateNotes(state, action) {
            const { healthcareProviderId, privateNotes } = action.payload;
            const currentNotes = state.privateNotesUpdates[healthcareProviderId]?.privateNotes ||
                getProviderPrivateNotes(state.providers, healthcareProviderId) || [];
            const notesWithMetadata = privateNotes.map((note) => {
                const existing = currentNotes.find((item) =>
                    item.healthcareProviderPrivateNoteId === note.healthcareProviderPrivateNoteId);
                if (!existing) return note;
                return {
                    ...existing,
                    ...note,
                    createdByUser: note.createdByUser || existing.createdByUser,
                    createdDate: note.createdDate || existing.createdDate,
                    lastEditedByUser: note.lastEditedByUser || existing.lastEditedByUser,
                    lastEditedDate: note.lastEditedDate || existing.lastEditedDate,
                };
            });
            state.privateNotesRevision += 1;
            state.privateNotesUpdates[healthcareProviderId] = {
                privateNotes: notesWithMetadata,
                revision: state.privateNotesRevision,
            };
            state.providers = reconcilePrivateNotes(state.providers, {
                [healthcareProviderId]: state.privateNotesUpdates[healthcareProviderId],
            });
        },
        setIsLoadingLookups(state, action) {
            state.isLoadingLookups = action.payload;
        },
        setIsSearching(state, action) {
            state.isSearching = action.payload;
        },
        setLookups(state, action) {
            state.lookups = action.payload;
        },
        setNonPreferredProviderDialog(state, action) {
            state.nonPreferredProviderDialog = action.payload;
        },
        setProviderPrintDialog(state, action) {
            state.providerPrintDialog = action.payload;
        },
        setProviders(state, action) {
            state.providers = action.payload;
        },
        setPreviousProviderIds(state, action) {
            state.previousProviderIds = action.payload;
        },
        setHasMoreResults(state, action) {
            state.hasMoreResults = action.payload;
        },
        setSort(state, action) {
            const { sortBy, sortDirection } = action.payload;
            state.sortBy = sortBy;
            state.sortDirection = sortDirection;
        },
    },
});

export const {
    resetState,
    resetResults,
    setIsLoadingLookups,
    setIsSearching,
    setLookups,
    setNonPreferredProviderDialog,
    setProviderPrintDialog,
    setProviders,
    setPreviousProviderIds,
    setHasMoreResults,
    setSort,
    setSessionId,
    setSearchRequestId,
    setPrivateNotesInteraction,
    startPrivateNotesEditing,
    editPrivateNotes,
    setPrivateNotesSaveState,
    acknowledgePrivateNotesChange,
    setPrivateNotesOpen,
    closePrivateNotes,
    updateProviderPrivateNotes,
} = slice.actions;

export const advancedProviderSearch = { reducer: slice.reducer, initialState };
export default advancedProviderSearch;

// Thunks
export const loadLookups =
    ({ patientId, clientId, facilityId }) =>
    async (dispatch, getState) => {
        dispatch(resetState());
        const sessionId = uuid();
        dispatch(setSessionId(sessionId));
        const { user } = getState().auth;
        await dispatch(setIsLoadingLookups(true));

        const result = await handleApiCall(
            async () =>
                await commonService.getApsLookups({
                    patientId,
                    clientId: clientId ?? user.originClientId ?? user.clients[0]?.clientId,
                    facilityId,
                }),
            {
                dispatch,
                errorMessage: 'Error loading APS data',
            },
        );

        if (getState().advancedProviderSearch.sessionId !== sessionId) return;
        if (result.wasSuccessful) {
            const lookups = { ...result, facilityId };
            delete lookups.wasSuccessful;
            await dispatch(setLookups(lookups));
        }

        await dispatch(setIsLoadingLookups(false));
    };

export const submitQuery =
    ({ patientId, clientId }, fetchingNextPage = false) =>
    async (dispatch, getState) => {
        const { query, previousProviderIds, sortBy, sortDirection, lookups, isSearching,
            sessionId, privateNotesRevision } =
            getState().advancedProviderSearch;
        if (fetchingNextPage && isSearching) return;
        const { user } = getState().auth;

        const requestId = uuid();
        dispatch(setSearchRequestId(requestId));
        if (!fetchingNextPage) dispatch(resetResults());
        dispatch(setIsSearching(true));

        const queryToSend = {
            patientId,
            searchName: query.searchName,
            address: {
                addressId: query.searchNear?.address?.addressId,
                address1: query.address1,
                zipcode: query.zipcode,
            },
            distance: query.distance?.value ?? undefined,
            gender: query.gender || Gender.None,
            insuranceCarrierId: query.insuranceCarrier?.value,
            insurancePlanType: query.insurancePlanType?.value,
            preferredByHealthcareProviderId: query.preferredByProvider?.value,
            insurancePlanSubType: query.insurancePlanSubType?.value,
            language: query.language?.value,
            affiliation: query.affiliation?.value,
            onlyProvidersAcceptingNewPatients: !!query.isAcceptingNewPatients,
            groupByOrganization: !!query.groupByOrganization,
            specialties: query.specialities?.map((speciality) => speciality.name),
            sortBy,
            sortDirection,
            clientId: clientId ?? user.originClientId ?? user.clients[0]?.clientId,
            facilityId: lookups?.facilityId,
            excludePersonIds: fetchingNextPage ? previousProviderIds : [],
            healthcareProviderTagIds: query.providerTags?.map((tag) => tag.value),
            tagFilterType: query?.tagFilterType,
        };

        const result = await handleApiCall(
            async () => await commonService.doApsSearch(queryToSend),
            {
                dispatch,
                errorMessage: 'Error searching for providers.',
            },
        );

        const current = getState().advancedProviderSearch;
        if (current.sessionId !== sessionId || current.searchRequestId !== requestId) return;

        if (result.wasSuccessful) {
            const idsToExclude = getExclusionIds(result.data);
            // the API returns the first page of results in triple (actually pageSize * 1.5 * 3) the size so we must check for that as well.
            // if there are no exactly 20 or 90 results then we know there are more results for infiniteScrollComponent to fetch
            const hasMoreResults =
                result.data.length === MIN_RESULT_SIZE ||
                result.data.length === MIN_RESULT_SIZE * 4.5
                    ? true
                    : false;

            const incoming = reconcilePrivateNotes(result.data, current.privateNotesUpdates, privateNotesRevision);
            dispatch(setProviders(fetchingNextPage ? [...(current.providers || []), ...incoming] : incoming));
            await dispatch(
                setPreviousProviderIds(
                    fetchingNextPage ? [...current.previousProviderIds, ...idsToExclude] : idsToExclude,
                ),
            );
            await dispatch(setHasMoreResults(hasMoreResults));
        } else {
            //this prevents the loader from within InfiniteScroll to appear
            dispatch(setHasMoreResults(false));
        }
        dispatch(setIsSearching(false));
    };

export const openProviderEditor = (healthcareProviderId) => async (dispatch, getState) => {
    const { sessionId } = getState().advancedProviderSearch;
    if (!await dispatch(confirmClosePrivateNotes()) ||
        getState().advancedProviderSearch.sessionId !== sessionId) return;

    const result = await dispatch(openDialog({
        type: DialogTypes.ProviderEditorInfoDialog,
        props: { healthcareProviderId },
    }));
    if (!result.wasSuccessful || result.wasCanceled ||
        getState().advancedProviderSearch.sessionId !== sessionId) return;

    const provider = result.returnArgs[0];
    if (provider?.healthcareProviderId !== healthcareProviderId ||
        !arePrivateNotesAvailable(provider.privateNotes)) return;

    dispatch(updateProviderPrivateNotes({
        healthcareProviderId,
        privateNotes: provider.privateNotes,
    }));
};

export const confirmClosePrivateNotes = () => async (dispatch, getState) => {
    const { privateNotesEditor: editor, sessionId } = getState().advancedProviderSearch;
    if (editor?.isSaving) return false;
    if (editor?.canEdit) {
        const { pendingEdit, draft } = editor;
        if (pendingEdit && !pendingEdit.noteText?.trim()) {
            dispatch(setPrivateNotesSaveState({ error: 'Enter text for the note or cancel the edit before closing.', canRetry: false }));
            return false;
        }
        if (pendingEdit || draft.trim()) {
            let notes = pendingEdit
                ? editor.notes.map((note) =>
                    (note.healthcareProviderPrivateNoteId || note.draftId) === pendingEdit.key
                        ? { ...note, noteText: pendingEdit.noteText.trim(), hasUnsavedChanges: true }
                        : note)
                : editor.notes;
            if (draft.trim()) notes = [...notes, { draftId: uuid(), healthcareProviderPrivateNoteId: 0, noteText: draft.trim(), isPinned: false }];
            dispatch(editPrivateNotes({ notes, draft: '', pendingEdit: null }));
        }
        try {
            const current = getState().advancedProviderSearch;
            if (current.sessionId !== sessionId || current.privateNotesEditor?.healthcareProviderId !== editor.healthcareProviderId) return false;
            const changes = getPrivateNotesChanges(current.privateNotesEditor.originalNotes, current.privateNotesEditor.notes);
            if (!changes.length) {
                dispatch(closePrivateNotes());
                return true;
            }
            dispatch(setPrivateNotesSaveState({ isSaving: true, error: null, canRetry: false }));
            const results = [];
            for (const change of changes) {
                results.push(await dispatch(mutatePrivateNote(change.method, {
                    healthcareProviderId: editor.healthcareProviderId,
                    ...change.data,
                })));
            }
            const afterSave = getState().advancedProviderSearch;
            if (afterSave.sessionId !== sessionId || afterSave.privateNotesEditor?.healthcareProviderId !== editor.healthcareProviderId) return false;
            const successfulResult = results.reduce((latest, result) =>
                result.wasSuccessful && arePrivateNotesAvailable(result.privateNotes) ? result : latest, null);
            if (successfulResult) {
                dispatch(acknowledgePrivateNotesChange({
                    privateNotes: successfulResult.privateNotes,
                    completedKeys: changes.filter((_, index) =>
                        results[index].wasSuccessful && arePrivateNotesAvailable(results[index].privateNotes))
                        .map((change) => change.key),
                }));
            }
            if (results.some((result) => !result.wasSuccessful || !arePrivateNotesAvailable(result.privateNotes))) {
                dispatch(setPrivateNotesSaveState({
                    error: 'Private notes could not be saved. Your pending changes are still here.',
                    canRetry: true,
                }));
                return false;
            }
        } finally {
            const current = getState().advancedProviderSearch;
            if (current.sessionId === sessionId && current.privateNotesEditor?.healthcareProviderId === editor.healthcareProviderId) {
                dispatch(setPrivateNotesSaveState({ isSaving: false }));
            }
        }
    }
    dispatch(closePrivateNotes());
    return true;
};

const mutatePrivateNote = (method, data) => async (dispatch, getState) => {
    const { sessionId } = getState().advancedProviderSearch;
    const result = await handleApiCall(
        () => commonService[method](data),
        {
            dispatch,
            errorMessage: 'Unable to save private note changes.',
            errorSeverity: ErrorSeverity.NONE,
        },
    );
    if (result.wasSuccessful && Array.isArray(result.privateNotes) &&
        getState().advancedProviderSearch.sessionId === sessionId) {
        dispatch(updateProviderPrivateNotes({
            healthcareProviderId: data.healthcareProviderId,
            privateNotes: result.privateNotes,
        }));
    }
    return result;
};

//helpers
const reconcilePrivateNotes = (providers, updates, sinceRevision = -1) => providers?.map((provider) => {
    if (provider.items) {
        return { ...provider, items: reconcilePrivateNotes(provider.items, updates, sinceRevision) };
    }
    const update = updates[provider.healthcareProviderId];
    return update && update.revision > sinceRevision
        ? { ...provider, privateNotes: update.privateNotes }
        : provider;
});

const getProviderPrivateNotes = (providers, healthcareProviderId) => {
    for (const provider of providers || []) {
        if (provider.items) {
            const notes = getProviderPrivateNotes(provider.items, healthcareProviderId);
            if (notes) return notes;
        } else if (provider.healthcareProviderId === healthcareProviderId) {
            return provider.privateNotes;
        }
    }
};

const getExclusionIds = (list) => {
    return list.reduce((arr, { items, personId }) => {
        //flatten list of ids if there are organization groupings with sub-items
        const newArr = items ? items.map(({ personId }) => personId) : [personId];

        return [...arr, ...newArr];
    }, []);
};
